"use client";

import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { Popover, Box } from "@mantine/core";
import { useId } from "@mantine/hooks";
import { useGlobalData } from "@/context/globalContext";
import { useFiltersStore } from "@/stores/filtersStore";
import {
  useFilterSearch,
  useTypewriterStrings,
  type FilterType,
  type Suggestion,
} from "@/hooks/useFilterSearch";
import useResetSearch from "@/hooks/useResetSearch";
import ImageUploadZone from "@/components/ImageUploadZone";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { SearchInput } from "./SearchInput";
import { SearchResults } from "./SearchResults";
import { useKeyboardNav } from "./useKeyboardNav";
import styles from "./styles.module.scss";

// Helper to detect image URLs
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)(\?.*)?$/i;
const isImageUrl = (str: string): boolean => {
  const trimmed = str.trim();
  if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) return false;
  return IMAGE_EXTENSIONS.test(trimmed);
};

// Mapping of filter types to URL parameter names
const FILTER_TYPE_TO_PARAM: Record<ExtendedFilterType, string> = {
  brand: "brands",
  vendor: "vendors",
  type: "types",
  region: "regions",
  country: "regions",
  keyword: "keywords",
  preset: "preset",
};

interface SearchBoxProps {
  navigateOnSelect?: boolean;
  navigateOnDropdownSelect?: boolean;
  useTypewriter?: boolean;
  onSelectionChange?: () => void;
  compactMode?: boolean;
  /** Pages with their own dedicated image upload flow (e.g. /appraise) set
   * this so a paste/drop/click here can't open this search bar's own image
   * search and navigate away — it would otherwise be a near-identical,
   * easily-confused UI sitting right next to the page's own upload area. */
  disableImageSearch?: boolean;
}

export interface SearchBoxRef {
  selectPill: (type: ExtendedFilterType, filter: Filter) => void;
  getSelected: () => SelectedFilter[];
  removePill: (label: string) => void;
}

const SearchBox = forwardRef<SearchBoxRef, SearchBoxProps>(
  (
    {
      navigateOnSelect = false,
      navigateOnDropdownSelect = false,
      useTypewriter = true,
      onSelectionChange,
      compactMode = false,
      disableImageSearch = false,
    },
    ref
  ) => {
    const { vendors, brands } = useGlobalData();
    const savedFilters = useFiltersStore((state) => state.savedFilters);
    const router = useRouter();
    const pathname = usePathname();
    const isSearchPage = pathname?.startsWith("/search");
    const startImageSearch = useImageSearchStore((state) => state.actions.startSearch);
    const clearImageSource = useImageSearchStore((state) => state.actions.clearSource);
    const setSearchText = useFiltersStore((state) => state.actions.setSearchText);
    const setSavedFilterId = useFiltersStore((state) => state.actions.setSavedFilterId);
    const resetSearch = useResetSearch();
    const popoverId = useId();

    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState<SelectedFilter[]>([]);
    const [opened, setOpened] = useState(false);
    const [highlighted, setHighlighted] = useState<number | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [showImageUpload, setShowImageUpload] = useState(false);

    // Notify parent component whenever selected filters change
    useEffect(() => {
      onSelectionChange?.();
    }, [selected, onSelectionChange]);

    // NavBar (and this search bar within it) persists across client-side
    // route changes rather than remounting, so image search opened on a
    // previous page would otherwise still be open after navigating to a
    // page like /appraise that disables it here.
    useEffect(() => {
      if (disableImageSearch && showImageUpload) {
        setShowImageUpload(false);
      }
    }, [disableImageSearch, showImageUpload]);

    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const dragCounter = useRef(0);

    const presetOptions = useMemo<Filter[]>(() => {
      return (savedFilters || []).map((saved) => ({
        id: saved.id,
        value: saved.id,
        label: saved.search_filter.name,
        type: "preset",
      }));
    }, [savedFilters]);

    // Use shared hooks
    const { suggestions } = useFilterSearch({
      brands,
      vendors,
      presets: presetOptions,
      query,
      maxPerType: 5,
      includeRegions: true,
      includePresets: true,
    });
    const brandStrings = useTypewriterStrings(brands);

    // Static placeholder when not using typewriter
    const staticPlaceholder = useMemo(() => {
      if (useTypewriter) return "";

      const defaults = ["Raf Simons", "Balenciaga", "Dior"];
      const shortBrands = (brands || []).filter((b) => b.label && b.label.length <= 12);
      const brandNames = shortBrands.map((brand) => brand.label).filter(Boolean);
      const examples = [...new Set([...brandNames, ...defaults])].slice(0, 3);

      return `Search for ${examples.join(", ")} and more`;
    }, [useTypewriter, brands]);

    useEffect(() => {
      if (query.length >= 2) {
        setOpened(true);
        setHighlighted(null);
      } else {
        setOpened(false);
        setHighlighted(null);
      }
    }, [query, suggestions.length]);

    // Helper to convert filters to URL params
    const filtersToParams = useCallback((filters: SelectedFilter[]) => {
      const params = new URLSearchParams();
      filters.forEach((filter) => {
        const paramName = FILTER_TYPE_TO_PARAM[filter.key];
        if (paramName) {
          params.append(paramName, String(filter.value));
        }
      });
      return params;
    }, []);

    const searchPath = useMemo(() => {
      const params = filtersToParams(selected);
      return `/search?${params.toString()}`;
    }, [selected, filtersToParams]);

    const navigateToSearch = useCallback(
      (path: string) => {
        setOpened(false);
        if (navigateOnSelect || navigateOnDropdownSelect) {
          const qs = path.split("?")[1] ?? "";
          if (qs.length > 0) {
            resetSearch();
          }
        }
        router.push(path);
      },
      [router, navigateOnSelect, navigateOnDropdownSelect, resetSearch]
    );

    // Helper to reset search UI state
    const resetSearchState = useCallback(() => {
      setQuery("");
      setOpened(false);
      setHighlighted(null);
      requestAnimationFrame(() => {
        const input = document.querySelector("input[class*='pillInputField']") as HTMLInputElement;
        input?.focus();
      });
    }, []);

    const handleSelect = useCallback(
      (s: Suggestion, fromDropdown: boolean = false) => {
        if (s.key === "preset") {
          router.push(`/search?preset=${s.option.value}`);
          resetSearchState();
          return;
        }

        // If it's from dropdown and navigateOnDropdownSelect is true
        if (fromDropdown && navigateOnDropdownSelect) {
          const filter: SelectedFilter = {
            key: s.key,
            value: s.option.value,
            label: s.option.label,
          };

          // Only navigate if there are NO existing selected filters
          if (selected.length === 0) {
            const params = filtersToParams([filter]);
            navigateToSearch(`/search?${params.toString()}`);
            return;
          }

          // If there are existing filters, just add this one to the list
          setSelected((prev) => {
            const exists = prev.find((p) => p.key === s.key && p.value === s.option.value);
            if (exists) return prev;
            return [...prev, filter];
          });
          resetSearchState();
          return;
        }

        // Original navigateOnSelect behavior (for pills)
        if (navigateOnSelect && !fromDropdown) {
          const filter: SelectedFilter = {
            key: s.key,
            value: s.option.value,
            label: s.option.label,
          };
          const allFilters = [...selected, filter];
          const params = filtersToParams(allFilters);
          navigateToSearch(`/search?${params.toString()}`);
          return;
        }

        // Default: just add to selected
        setSelected((prev) => {
          const exists = prev.find((p) => p.key === s.key && p.value === s.option.value);
          if (exists) return prev;
          return [...prev, { key: s.key, value: s.option.value, label: s.option.label }];
        });
        resetSearchState();
      },
      [
        navigateOnSelect,
        navigateOnDropdownSelect,
        filtersToParams,
        navigateToSearch,
        resetSearchState,
        selected,
        router,
      ]
    );

    const handleKeywordAdd = useCallback(() => {
      const q = query.trim();
      if (!q) return;

      // Check if it's an image URL - trigger image search instead
      if (!disableImageSearch && isImageUrl(q)) {
        const fakeItem = { imageurl: q } as SearchResultItem;
        startImageSearch(fakeItem);
        router.push("/search?clearFilters=1");
        resetSearchState();
        return;
      }

      if (navigateOnSelect || navigateOnDropdownSelect) {
        if (isSearchPage) {
          // Already on the search page: apply the keyword in-place without resetting all filters.
          // Clears the saved filter selection so the keyword runs against the current conditions
          // without modifying (or being tied to) a saved filter.
          setSavedFilterId(undefined);
          setSearchText(q);
          resetSearchState();
          return;
        }
        navigateToSearch(`/search?keywords=${encodeURIComponent(q)}`);
        setSearchText(q);
        return;
      }

      setSelected((prev) => [...prev, { key: "keyword", value: q, label: q }]);
      resetSearchState();
    }, [
      query,
      navigateOnSelect,
      navigateOnDropdownSelect,
      navigateToSearch,
      resetSearchState,
      startImageSearch,
      router,
      setSearchText,
      isSearchPage,
      setSavedFilterId,
      disableImageSearch,
    ]);

    const handleImageSearchComplete = useCallback(() => {
      setShowImageUpload(false);
      router.push("/search");
    }, [router]);

    // Drag handlers to detect image drag and switch to image upload mode
    const hasImageFile = useCallback((e: React.DragEvent) => {
      const items = e.dataTransfer?.items;
      if (!items) return false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) return true;
      }
      return false;
    }, []);

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (!disableImageSearch && hasImageFile(e)) {
          setShowImageUpload(true);
        }
      },
      [hasImageFile, disableImageSearch]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        // Don't hide here - let ImageUploadZone handle it
      }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    // Navigate with the highlighted suggestion (if any) plus existing filters
    const navigateWithHighlighted = useCallback(() => {
      if (highlighted != null && suggestions[highlighted]) {
        const s = suggestions[highlighted];
        const filter: SelectedFilter = {
          key: s.key,
          value: s.option.value,
          label: s.option.label,
        };
        const exists = selected.find((p) => p.key === filter.key && p.value === filter.value);
        const allFilters = exists ? selected : [...selected, filter];
        const params = filtersToParams(allFilters);
        navigateToSearch(`/search?${params.toString()}`);
        return true;
      }
      return false;
    }, [highlighted, suggestions, selected, filtersToParams, navigateToSearch]);

    // Function to handle pill selection from external components
    const handlePillSelect = useCallback(
      (type: ExtendedFilterType, filter: Filter) => {
        if (type === "keyword") return; // Keywords handled separately
        const suggestion: Suggestion = {
          key: type as FilterType,
          option: filter,
        };
        handleSelect(suggestion, false); // Pass false for fromDropdown (it's from pills)
      },
      [handleSelect]
    );

    // Expose methods to parent component via ref
    const handleRemove = useCallback((label: string) => {
      setSelected((prev) => prev.filter((f) => f.label !== label));
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        selectPill: handlePillSelect,
        getSelected: () => selected,
        removePill: (label: string) => handleRemove(label),
      }),
      [handlePillSelect, selected, handleRemove]
    );

    // Keyboard navigation hook
    const { onKeyDown } = useKeyboardNav({
      opened,
      suggestions,
      highlighted,
      query,
      searchPath,
      selectedLength: selected.length,
      onSetOpened: setOpened,
      onSetHighlighted: setHighlighted,
      onSelect: handleSelect,
      onKeywordAdd: handleKeywordAdd,
      onNavigate: navigateToSearch,
      onNavigateWithHighlighted: navigateWithHighlighted,
    });

    return (
      <Box
        w="100%"
        maw={850}
        style={{ position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        <Popover
          opened={opened}
          position="bottom-start"
          width={containerRef.current?.clientWidth || 100}
          withinPortal
          withArrow
          offset={4}
          onChange={setOpened}
          trapFocus={false}
          id={popoverId}
        >
          <Popover.Target>
            <SearchInput
              query={query}
              selected={selected}
              isFocused={isFocused}
              compactMode={compactMode}
              useTypewriter={useTypewriter}
              brandStrings={brandStrings}
              staticPlaceholder={staticPlaceholder}
              containerRef={containerRef}
              onQueryChange={setQuery}
              onFocus={() => {
                setIsFocused(true);
                if (query.length >= 2 && suggestions.length > 0) setOpened(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                setTimeout(() => setOpened(false), 150);
              }}
              onKeyDown={onKeyDown}
              onRemove={handleRemove}
              onImageSearchToggle={() => {
                if (disableImageSearch) return;
                if (!showImageUpload) clearImageSource();
                setShowImageUpload(!showImageUpload);
              }}
              onNavigate={navigateToSearch}
              onNavigateWithHighlighted={navigateWithHighlighted}
              onKeywordAdd={handleKeywordAdd}
              searchPath={searchPath}
            />
          </Popover.Target>

          <Popover.Dropdown p={0}>
            <SearchResults
              opened={opened}
              suggestions={suggestions}
              highlighted={highlighted}
              scrollRef={scrollRef}
              onHighlight={setHighlighted}
              onSelect={handleSelect}
            />
          </Popover.Dropdown>
        </Popover>

        {/* Unmounted entirely (not just CSS-hidden) when disableImageSearch is
            set — /appraise has its own separate crop-and-upload flow right
            next to this one, and only a hard guarantee that this one isn't
            even in the DOM rules out any chance of the two being confused. */}
        {!disableImageSearch && (
          <Box className={styles.imageSearchBox} data-visible={showImageUpload}>
            <ImageUploadZone
              onSearchComplete={handleImageSearchComplete}
              onClose={() => setShowImageUpload(false)}
              label=""
            />
          </Box>
        )}
      </Box>
    );
  }
);

SearchBox.displayName = "SearchBox";

export default SearchBox;
